// Contract of the Power admin route for regulatory API drift: per ecosystem, one row per
// operation the regulatory catalog expects, with the state of this build against it. The
// regulatory catalog generator writes the snapshot at build time, Power embeds it and serves it,
// and the admin SPA decodes it; nothing reads the official source at runtime.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`, so a member
// the contract does not define is refused rather than dropped.
import { Schema } from 'effect';
import { Ecosystem, IsoDateTimeUtc } from '@finnest/core/effect';
import { HttpUrl } from '../_shared/formats.ts';

const MAX_LABEL_CHARS = 120;
const MAX_PATH_CHARS = 300;
const MAX_TEXT_CHARS = 500;
// The catalogs expect 143 OFB and 167 OPIN operations; the cap leaves room for new families.
export const REGULATORY_DRIFT_ROWS_CAP = 1000;

const boundedText = (max: number): Schema.String =>
  Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(max));

const ApiPath = Schema.String.check(Schema.isStartingWith('/'), Schema.isMaxLength(MAX_PATH_CHARS));

export const EndpointMethod = Schema.Literals(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export type EndpointMethod = typeof EndpointMethod.Type;

/** The major version segment of a served path (`v2`), or none when the path carries no `/vN`. */
export const ServedVersion = Schema.Union([
  Schema.String.check(Schema.isPattern(/^v\d+$/u, { expected: 'a major version such as v2' })),
  Schema.Literal('unversioned'),
]);
export type ServedVersion = typeof ServedVersion.Type;

export const DriftReason = Schema.Literals(['version-mismatch', 'catalog-issue']);
export type DriftReason = typeof DriftReason.Type;

/**
 * How this build stands against one expected operation, by precedence: the source is blocked,
 * nothing serves the method and path in any major version, it is served in another major version
 * or its family has a blocking catalog issue, or it is served exactly.
 */
export const EndpointState = Schema.Union([
  Schema.TaggedStruct('covered', { servedPath: ApiPath, servedVersion: ServedVersion }),
  Schema.TaggedStruct('drift', {
    servedPath: ApiPath,
    servedVersion: ServedVersion,
    reason: DriftReason,
    detail: boundedText(MAX_TEXT_CHARS),
  }),
  Schema.TaggedStruct('missing', {}),
  Schema.TaggedStruct('source-blocked', { detail: boundedText(MAX_TEXT_CHARS) }),
]).annotate({
  title: 'PowerAdminRegulatoryEndpointState',
  description:
    'Whether this build serves an expected regulatory operation as the source states it.',
});
export type EndpointState = typeof EndpointState.Type;
export type EndpointStateTag = EndpointState['_tag'];

/**
 * Where the source stands the family's version: the version in force, or the previous major the
 * participant keeps serving during the coexistence period the source sets for a new major.
 */
export const SourceLifecycle = Schema.Literals(['current', 'deprecated']);
export type SourceLifecycle = typeof SourceLifecycle.Type;

/** The family fields every row of that family repeats. */
const FamilyFields = {
  family: boundedText(MAX_LABEL_CHARS),
  phase: boundedText(MAX_LABEL_CHARS),
  sourceVersion: boundedText(MAX_LABEL_CHARS),
  sourceLifecycle: SourceLifecycle,
  sourceUrl: HttpUrl,
  sourceCapturedAt: IsoDateTimeUtc,
};

/** One operation the source expects: its method, versioned path and summary. */
export const ExpectedOperation = Schema.Struct({
  method: EndpointMethod,
  path: ApiPath,
  summary: boundedText(MAX_TEXT_CHARS),
});
export type ExpectedOperation = typeof ExpectedOperation.Type;

export const RegulatoryEndpointRow = Schema.Struct({
  ...FamilyFields,
  ...ExpectedOperation.fields,
  state: EndpointState,
}).annotate({
  title: 'PowerAdminRegulatoryEndpointRow',
  description: 'One expected regulatory operation and how this build serves it.',
});
export type RegulatoryEndpointRow = typeof RegulatoryEndpointRow.Type;

export const RegulatoryEndpointSnapshot = Schema.Struct({
  ecosystem: Ecosystem,
  generatedAt: IsoDateTimeUtc,
  rows: Schema.Array(RegulatoryEndpointRow).check(Schema.isMaxLength(REGULATORY_DRIFT_ROWS_CAP)),
}).annotate({
  title: 'PowerAdminRegulatoryEndpointSnapshot',
  description:
    'The regulatory catalog snapshot of one ecosystem, generated with this build: one row per expected operation.',
});
export type RegulatoryEndpointSnapshot = typeof RegulatoryEndpointSnapshot.Type;

/** Whether the source of a family can be implemented against, or why not. */
export const FamilyAvailability = Schema.Union([
  Schema.TaggedStruct('open', {}),
  Schema.TaggedStruct('blocked', { detail: boundedText(MAX_TEXT_CHARS) }),
]);
export type FamilyAvailability = typeof FamilyAvailability.Type;

/** One family of the regulatory catalog as the projection reads it. */
export const ProjectionFamily = Schema.Struct({
  ...FamilyFields,
  availability: FamilyAvailability,
  /** The family's blocking catalog or coverage issues; none when the drift report names none. */
  driftIssues: Schema.Array(boundedText(MAX_TEXT_CHARS)),
  operations: Schema.Array(ExpectedOperation),
});
export type ProjectionFamily = typeof ProjectionFamily.Type;
